import { FlowjoCommons } from '@portal/portal-core';

import * as UserClientActions from '../actions';

const { Net } = FlowjoCommons;

export const INVITE_TOKEN_ID = 'inviteTokensDismissed';

interface DispatchStore {
  dispatch: (action: unknown) => unknown;
}

/**
 * processes email verification token if exists in url parameter,
 * else authenticates user with jwt token if token is available
 *
 * @param store - redux store
 * @param inviteToken - invite token
 * @returns whether or not the action was dispatched
 */
export function interceptUserInviteAcceptanceFromUrl(
  store: DispatchStore,
  inviteToken: unknown,
): boolean {
  if (inviteToken) {
    // Preserve original single-argument call; the typed action creator expects
    // (userId, inviteId), so narrow it to the shape used here.
    const acceptInvite = UserClientActions.acceptInvite as (token: unknown) => unknown;
    const action = acceptInvite(inviteToken);
    store.dispatch(action);
    return true;
  }
  return false;
}

function convertQueryPairsStringToObj(stringOfPairs: string | string[]): Record<string, string> {
  const paramObj: Record<string, string> = {};
  if (stringOfPairs) {
    for (let i = 0; i < stringOfPairs.length; i += 1) {
      const pair = stringOfPairs[i].split('=');
      if (pair[0]) {
        paramObj[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
      }
    }
  }
  return paramObj;
}

export function convertQueryStringToKeyValues(searchString = ''): Record<string, string> {
  let allParams: string = '';
  if (!searchString) {
    allParams = window.location.search;
    if (!allParams) {
      return {};
    }
  }
  if (searchString) {
    allParams = searchString;
  }
  if (allParams.includes('?')) {
    allParams = allParams.replace('?', '');
  }
  let pairs: string | string[] = '';
  if (typeof allParams === 'string' && allParams.indexOf('&')) {
    pairs = allParams.split('&');
  } else {
    pairs = allParams;
  }
  return convertQueryPairsStringToObj(pairs);
}

// Wrapped `succeeded`/`failed` interface returned to callers.
export interface WrappedHttpResult {
  succeeded?: boolean;
  failed?: boolean;
  error?: unknown;
  data?: unknown;
}

// Shape of the resolved value from `Net.sendJson` (typed as `unknown` upstream).
interface HttpResponse {
  code: number;
  data: string;
}

// Wraps HTTP request in safe `succeeded`/`failed` interface
export const wrapHttpRequest = (promise: Promise<unknown>): Promise<WrappedHttpResult> =>
  promise
    .then((response) => {
      const { code, data } = response as HttpResponse;
      return code === Net.reponses.OK
        ? { succeeded: true, data: JSON.parse(data).result }
        : { failed: true, error: code, data: JSON.parse(data).error };
    })
    .catch((err: unknown) => ({ failed: true, error: err }));

interface UserClientConfig {
  // Optional at this boundary: consumer saga configs thread `getAuthToken` in at
  // runtime but type it optionally (some carry no static shape for it).
  getAuthToken?: () => string | null | undefined;
}

export const sendAction = (
  config: UserClientConfig,
  // Accept any object action (consumer sagas pass typed action interfaces, which
  // are not assignable to `Record<string, unknown>`). Forwarded untyped to Net.
  action: object,
  url?: string,
): Promise<WrappedHttpResult> => {
  // Non-null assertion preserves the original runtime call (throws if absent);
  // `getAuthToken` is optional only at the type boundary.
  const authToken = config.getAuthToken!();
  if (authToken) {
    const headers = { Authorization: `Bearer ${authToken}` };
    return wrapHttpRequest(Net.sendJson(url, action, headers));
  }
  return wrapHttpRequest(Net.sendJson(url, action));
};
